import React, { FC, useMemo, useState } from 'react';
import { Button, Drawer, Form, InputNumber } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { Notification, SelectWithSearch, SuccessCard } from 'common/components';
import { objectIsEmpty, rules } from 'common/helpers';
import { useInventoryContext } from 'common/context';
import { DEFAULT_VALUE_0, DEFAULT_VALUE_1, LIST_LIMIT_0 } from 'common/config';
import { IFormValue } from 'common/models';
import { ArrowLeftLong, Close } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { InventoryAvailableCount } from 'entities/Inventory/components';
import {
  getInventoryTransferCardSubdivisionOptions,
  getInventoryTransferCardUserOptions,
  inventoryCount,
} from 'entities/Inventory/Inventory.helper';
import { EInventoryActionType } from 'entities/Inventory/Inventory.enum';
import { getUserName } from 'entities/User/User.helper';
import { GoodsCardMin } from 'entities/Goods/components';
import { filterInventoryListByCount, selectInventory, selectInventoryUsers } from 'entities/Inventory/Inventory.slice';
import { createInventoryAction, selectIsInventoryActionLoading } from 'entities/InventoryAction/InventoryAction.slice';
import { getSubdivisionList, selectSubdivisionList } from 'entities/Subdivision/Subdivision.slice';
import { getUserInventoryStats, selectCurrentUser } from 'entities/User/User.slice';

export const InventoryTransferCard: FC = () => {
  const dispatch = useAppDispatch();
  // inventory
  const inventory = useAppSelector(selectInventory);
  const inventoryUsers = useAppSelector(selectInventoryUsers);
  // inventory action
  const isInventoryActionLoading = useAppSelector(selectIsInventoryActionLoading);
  // subdivision
  const subdivisionList = useAppSelector(selectSubdivisionList);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [userId, setUserId] = useState<number | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [openSuccessCard, setOpenSuccessCard] = useState<boolean>(false);
  const [form] = useForm();
  const formValues = useWatch([], form);
  const { isNestedCard, setIsNestedCard, openInventoryTransferCard, setOpenInventoryTransferCard } = useInventoryContext();

  const availableCount = inventoryCount.availableCount(inventory);
  const userOptions = useMemo(() => {
    // @ts-ignore
    return getInventoryTransferCardUserOptions(inventoryUsers, inventory?.user?.id, currentUser?.id);
  }, [inventoryUsers, inventory?.user?.id, currentUser?.id]);
  const subdivisionOptions = useMemo(() => {
    return getInventoryTransferCardSubdivisionOptions(subdivisionList);
  }, [subdivisionList]);
  const selectedUser = inventoryUsers.find((user: any) => user.id === userId);
  const submitDisabled = formValues && (objectIsEmpty(formValues) || Object.values(formValues).some((value) => !value));

  const onUserChange = async (id: number | null) => {
    form.resetFields(['subdivisionId']);
    setUserId(id);
    const subdivisionList = await dispatch(
      getSubdivisionList({
        userId: id ? id : undefined,
        limit: LIST_LIMIT_0,
      }),
    );

    if (subdivisionList && subdivisionList.length === DEFAULT_VALUE_1) {
      form.setFieldValue('subdivisionId', subdivisionList[0].id);
    }
  };

  const onClose = () => {
    if (isInventoryActionLoading) {
      return;
    }

    setOpenInventoryTransferCard(false);

    setIsNestedCard(false);
    form.resetFields();
  };

  const onSuccessCardClose = () => {
    setOpenSuccessCard(false);
    form.resetFields();
  };

  const onSubmit = (values: IFormValue) => {
    const { receiverId, subdivisionId, count } = values;

    if (inventory) {
      dispatch(
        createInventoryAction({
          inventoryId: inventory.id,
          type: EInventoryActionType.Transfer,
          receiverId,
          subdivisionId,
          count,
          onSuccess: () => {
            setOpenInventoryTransferCard(false);
            setOpenSuccessCard(true);
            dispatch(filterInventoryListByCount());
            dispatch(getUserInventoryStats({}));
          },
        }),
      );
    }
  };

  return (
    <>
      <Drawer
        rootClassName={`redesign drawer ${isNestedCard ? 'nested' : ''}`}
        open={openInventoryTransferCard}
        onClose={onClose}
        width={800}
        destroyOnClose
        closeIcon={isNestedCard ? <ArrowLeftLong /> : <Close />}
        classNames={{ footer: 'flex align-center justify-between' }}
        footer={[
          <Button key="cancel" className="button-lg secondary" onClick={onClose} loading={isInventoryActionLoading}>
            Отмена
          </Button>,
          <Button
            key="transfer"
            className="button-lg primary"
            loading={isInventoryActionLoading}
            onClick={form.submit}
            disabled={submitDisabled}
          >
            Передать оборудование
          </Button>,
        ]}
      >
        <div className="ff-regular fs-xxl mb-14">Передача оборудования другому сотруднику</div>

        <div className="mb-6">
          <GoodsCardMin good={inventory?.good} />
        </div>

        {inventory?.count && (
          <div className="mb-9">
            <InventoryAvailableCount availableCount={availableCount} totalCount={inventory.count} />
          </div>
        )}

        <Form layout="vertical" requiredMark={false} form={form} onFinish={onSubmit}>
          <Form.Item className="mb-9" label="Новый ответственный*" name="receiverId" rules={[rules.required()]}>
            <SelectWithSearch placeholder="Выберите сотрудника" options={userOptions} onChange={onUserChange} />
          </Form.Item>

          <Form.Item className="mb-9" label="Подразделение*" name="subdivisionId" rules={[rules.required()]}>
            <SelectWithSearch placeholder="Выберите подразделение" options={subdivisionOptions} />
          </Form.Item>

          <Form.Item
            className="mb-9"
            label="Количество оборудования*"
            name="count"
            rules={[
              rules.required(),
              () => ({
                validator(_, value) {
                  if (value > availableCount || value <= DEFAULT_VALUE_0) {
                    return Promise.reject(new Error('Невозможно указать введённое количество.'));
                  }

                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber controls={false} suffix="шт" onChange={(value) => setCount(value as number)} />
          </Form.Item>
        </Form>

        <div className="mb-9">
          <Notification
            type="success"
            title="Передача оборудования сотруднику, зарегистрированному в системе"
            description="Назначьте ответственного за инструмент. Сотруднику придет запрос на подтверждение передачи. После подтверждения инструмент перейдет на баланс нового ответственного."
          />
        </div>
      </Drawer>

      <SuccessCard
        open={openSuccessCard}
        content={`Запрос о передаче оборудования сотруднику ${getUserName.fullLastNameAndInitials(
          selectedUser?.lastName,
          selectedUser?.firstName,
          selectedUser?.patronymic,
        )} в количестве ${count} шт успешно отправлен`}
        subtitle="Готово!"
        btnTitle="Назад к списку"
        onClose={onSuccessCardClose}
        onConfirm={onSuccessCardClose}
      />
    </>
  );
};
